import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
    SkipBack,
    SkipForward,
    Play,
    Pause,
    RefreshCw,
    Maximize2,
    Wifi,
    BookOpen,
    Sparkles,
    ChevronRight,
    Brain,
    ArrowRight,
    Square,
    X,
    Volume2,
    AlertTriangle,
    Zap,
    Mic,
} from "lucide-react";
import { gaseousLesson, gaseousStoryboard } from "../lib/gaseousLesson";
import {
    loadStoredStoryboard,
    saveStoredStoryboard,
} from "../lib/storyboard";
import { useSessionChannel } from "../hooks/useSessionChannel";
import { VoiceAssistantModal } from "../components/VoiceAssistantModal";
import { getBackendUrl } from "../lib/utils";

/* ---------- atoms ---------- */
const Pill = ({ children, tone = "neutral" }) => {
    const tones = {
        neutral: "bg-black/[0.04] text-[#4a5662]",
        primary: "bg-[#E0F4F4] text-[#2E8E9A]",
        warm: "bg-[#FFF1E5] text-[#C75730]",
        ok: "bg-[#DCEFEC] text-[#2D7A6A]",
    };
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold ${tones[tone]}`}
        >
            {children}
        </span>
    );
};

/* ---------- main ---------- */
const TeacherPanelDeck = () => {
    const sessionId = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("sessionId") || `gaseous-${Date.now()}`;
    }, []);

    const [joinCode, setJoinCode] = useState(() => {
        try {
            return (
                sessionStorage.getItem(`sparkl-join-code-${sessionId}`) ||
                localStorage.getItem(`sparkl-join-code-${sessionId}`) ||
                null
            );
        } catch {
            return null;
        }
    });
    const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);

    // Ensure a join code exists for this sessionId — register it server-side
    // if it isn't already, so the chip always has something to display even
    // when the teacher panel was opened directly with a URL sessionId.
    useEffect(() => {
        if (joinCode) return;
        const base = getBackendUrl();
        if (!base) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(
                    `${base}/axis-ai/v1/octane/sessions/${encodeURIComponent(sessionId)}/ensure`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ lesson_id: "gaseous-state" }),
                    },
                );
                if (!r.ok) return;
                const data = await r.json();
                if (cancelled) return;
                setJoinCode(data.join_code);
                try {
                    sessionStorage.setItem(
                        `sparkl-join-code-${sessionId}`,
                        data.join_code,
                    );
                    localStorage.setItem(
                        `sparkl-join-code-${sessionId}`,
                        data.join_code,
                    );
                } catch {
                    /* noop */
                }
            } catch {
                /* offline */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [sessionId, joinCode]);

    const iframeRef = useRef(null);
    const [bcReady, setBcReady] = useState(false);

    // Deck state (driven by the iframe via BroadcastChannel)
    // slide_no is 1-based, fragment_no is 0-based — matches deck.js getIndices().
    // total_fragments comes from the bridge (counts [data-fragment-index]
    // nodes in the current section) — 0 means the slide has no animated
    // fragments, so we should not wait for any to be revealed.
    const [deckState, setDeckState] = useState({
        slide_no: 1,
        fragment_no: 0,
        total_fragments: 0,
    });
    const [popup, setPopup] = useState(null); // {popup_type, question}
    const [playing, setPlaying] = useState(true);
    const [autoPilot, setAutoPilot] = useState(false);
    // Most recent signal from the bridge telling us "this fragment is done".
    // - kind: "audio_done" → audio finished (+500ms grace was already added).
    // - kind: "silent"     → no audio attached; use a short fallback dwell.
    const [advanceSignal, setAdvanceSignal] = useState(null);
    const hasAudio = (sb) =>
        Array.isArray(sb) && sb.some((e) => e?.audio);
    const [hasStoryboard, setHasStoryboard] = useState(() =>
        hasAudio(loadStoredStoryboard("gaseous-state")),
    );

    /* ---- seed the pre-authored storyboard so concept-check popups
       fire even before the teacher uploads audio. Storage entries with
       empty `audio` strings keep audio playback off but still trigger
       the concept_check / engagement_check overlays inside the deck. */
    useEffect(() => {
        if (!loadStoredStoryboard("gaseous-state")) {
            saveStoredStoryboard("gaseous-state", gaseousStoryboard);
        }
    }, []);

    /* ---- session channel: BroadcastChannel + WebSocket ---- */
    const channel = useSessionChannel(sessionId);
    useEffect(() => {
        setBcReady(true);
    }, []);
    useEffect(() => {
        if (!channel?.onMessage) return;
        return channel.onMessage((m) => {
            if (m.role === "teacher") return;
            if (m.type === "DECK_STATE") {
                setDeckState({
                    slide_no: m.slide_no || 1,
                    fragment_no: Math.max(0, m.fragment_no || 0),
                    total_fragments: m.total_fragments || 0,
                });
            } else if (m.type === "DECK_POPUP") {
                setPopup({
                    popup_type: m.popup_type,
                    question: m.question,
                    slide_no: m.slide_no,
                    fragment_no: m.fragment_no,
                });
            } else if (m.type === "DECK_POPUP_CLEARED") {
                setPopup(null);
            } else if (m.type === "DECK_AUDIO_DONE") {
                setAdvanceSignal({
                    kind: "audio_done",
                    slide_no: m.slide_no,
                    fragment_no: m.fragment_no,
                    ts: Date.now(),
                });
            } else if (m.type === "DECK_FRAGMENT_SILENT") {
                setAdvanceSignal({
                    kind: "silent",
                    slide_no: m.slide_no,
                    fragment_no: m.fragment_no,
                    ts: Date.now(),
                });
            }
        });
    }, [channel]);

    /* ---- watch localStorage so the navigator badge updates after upload ---- */
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === "sparkl-storyboard-gaseous-state-v1") {
                try {
                    const parsed = e.newValue ? JSON.parse(e.newValue) : null;
                    setHasStoryboard(hasAudio(parsed));
                } catch {
                    setHasStoryboard(false);
                }
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    /* ---- send a command on the session channel ---- */
    const sendCommand = (type, extra = {}) => {
        channel?.send?.({ type, role: "teacher", ...extra });
    };

    /* ---- auto-advance fragments / slides while playing ----
       Driven entirely by signals from the bridge:
         - DECK_AUDIO_DONE  → bridge already waited 500ms after the audio
                              ended. Advance immediately.
         - DECK_FRAGMENT_SILENT → no audio attached to this fragment. Use a
                              short dwell so silent fragments don't stall.
       Both honour the same guards (playing, no popup, not past the last
       slide's last fragment, and slide-boundary respect when autopilot is
       off). */
    const SILENT_DWELL_MS = 3000;
    const totalFragmentsForTimer = deckState.total_fragments || 0;
    // deck.js fragment_no is 0-based; total_fragments is a count. So "at the
    // last fragment of the slide" means fragment_no >= total - 1.
    const atLastFragmentOfSlide =
        totalFragmentsForTimer === 0 ||
        deckState.fragment_no >= totalFragmentsForTimer - 1;
    const atLastSlide = deckState.slide_no >= gaseousLesson.slides.length;
    const canAutoAdvance =
        playing &&
        !popup &&
        !(atLastSlide && atLastFragmentOfSlide) &&
        (autoPilot || !atLastFragmentOfSlide);

    useEffect(() => {
        if (!advanceSignal) return;
        if (!canAutoAdvance) return;
        // Ignore stale signals that don't match the current (slide, fragment).
        if (
            advanceSignal.slide_no !== deckState.slide_no ||
            advanceSignal.fragment_no !== deckState.fragment_no
        ) {
            return;
        }
        const delay =
            advanceSignal.kind === "audio_done" ? 0 : SILENT_DWELL_MS;
        const id = setTimeout(() => {
            sendCommand("DECK_NEXT");
        }, delay);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advanceSignal, canAutoAdvance, deckState.slide_no, deckState.fragment_no]);

    /* ---- keyboard nav at the React level: forward arrows to BOTH surfaces ----
       deck.js's own keyboard handler is disabled inside the teacher iframe (see
       gaseous-state.html role=teacher-preview). Pressing Left/Right or Space
       here goes through the BroadcastChannel so the student deck advances too.
    */
    useEffect(() => {
        const onKey = (e) => {
            // Don't hijack typing inside inputs / textareas / contenteditable.
            const tag = (e.target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || e.target?.isContentEditable)
                return;
            if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
                e.preventDefault();
                sendCommand("DECK_NEXT");
            } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
                e.preventDefault();
                sendCommand("DECK_PREV");
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ---- controls ---- */
    const next = () => sendCommand("DECK_NEXT");
    const prev = () => sendCommand("DECK_PREV");
    const goto = (slideNo) =>
        sendCommand("DECK_GOTO", { slide: slideNo, fragment: 0 });
    const replay = () => sendCommand("DECK_PLAY_AUDIO");
    const togglePlay = () => {
        if (playing) {
            sendCommand("DECK_PAUSE_AUDIO");
            setPlaying(false);
        } else {
            sendCommand("DECK_PLAY_AUDIO");
            setPlaying(true);
        }
    };
    const dismissPopup = () => {
        sendCommand("DECK_DISMISS_POPUP");
        setPopup(null);
    };
    const requestStudentFullscreen = () => {
        // The student window opens the deck HTML directly; the bridge inside
        // listens for DECK_AUTO_FULLSCREEN over BroadcastChannel.
        sendCommand("DECK_AUTO_FULLSCREEN");
        try {
            const w = window.open("", "sparkl-student-display");
            if (w) {
                w.postMessage({ type: "sparkl:fullscreen" }, "*");
            }
        } catch {
            /* noop */
        }
    };
    const endSession = () => {
        if (!window.confirm("End this session and return to the home screen?"))
            return;
        try {
            // Try to close the student window gracefully.
            const w = window.open("", "sparkl-student-display");
            if (w) w.close();
        } catch {
            /* noop */
        }
        window.location.href = "/";
    };

    /* ---- overlays (real-life examples, MCQ, subjective, activity, recap) ----
       Drives a fullscreen overlay on the student board (mirrored locally so the
       teacher sees the same thing). Picked from the floating "Review" panel
       and the "Real-life hooks" cards. */
    const [overlay, setOverlay] = useState(null);

    const pushOverlay = (next) => {
        setOverlay(next);
        sendCommand("DECK_OVERLAY", { overlay: next });
    };
    const clearOverlay = () => pushOverlay(null);

    /* ---- current slide metadata (from gaseousLesson) ---- */
    // deck.js slide_no is 1-based; our slides array is 0-based.
    const slideIdx = Math.min(
        Math.max(deckState.slide_no - 1, 0),
        gaseousLesson.slides.length - 1,
    );
    const slide = gaseousLesson.slides[slideIdx];
    const totalFragments = deckState.total_fragments || 0;

    /* ---- recommendation for the current slide ---- */
    const recs = gaseousLesson.aiRecommendations?.[slide?.id] || [];

    /* ---- iframe URL — teacher preview, muted ---- */
    const iframeSrc = `${gaseousLesson.deckUrl}?role=teacher-preview&sessionId=${encodeURIComponent(
        sessionId,
    )}`;

    /* ---- render ---- */
    return (
        <div className="min-h-screen ambient-bg grain flex">
            {/* ----- Left: Slide navigator ----- */}
            <aside className="w-[320px] shrink-0 h-screen sticky top-0 border-r border-black/[0.04] bg-white/60 backdrop-blur-xl flex flex-col">
                <div className="px-5 pt-6 pb-4 border-b border-black/[0.04]">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg sparkl-gradient flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                                S
                            </span>
                        </div>
                        <Link
                            to="/"
                            className="font-semibold text-base tracking-tight hover:opacity-80"
                            data-testid="back-to-home-link"
                        >
                            Sparkl<span className="text-[#5BC2CC]">.</span>
                        </Link>
                        <span className="ml-auto">
                            <Pill tone="warm">External · deck.js</Pill>
                        </span>
                    </div>
                    <h2 className="text-base font-semibold leading-tight">
                        {gaseousLesson.sessionName}
                    </h2>
                    <p className="text-[11px] text-[#4a5662] mt-0.5">
                        {gaseousLesson.grade} · {gaseousLesson.chapter}
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                        <Pill tone={hasStoryboard ? "ok" : "neutral"}>
                            <Volume2 className="w-3 h-3" />
                            {hasStoryboard ? "Audio loaded" : "No audio"}
                        </Pill>
                        <Link
                            to="/audio-studio"
                            data-testid="open-audio-studio-from-deck"
                            className="text-[10px] uppercase tracking-[0.18em] text-[#4a5662] underline-offset-2 hover:underline"
                        >
                            Audio Studio →
                        </Link>
                    </div>
                </div>
                <div
                    className="flex-1 overflow-y-auto px-3 py-3 space-y-2"
                    data-testid="deck-slide-navigator"
                >
                    {gaseousLesson.slides.map((s, i) => {
                        const isCurrent = i === slideIdx;
                        return (
                            <button
                                key={s.id}
                                data-testid={`deck-nav-slide-${i + 1}`}
                                onClick={() => goto(i + 1)}
                                className={`w-full text-left px-3 py-2.5 rounded-xl border-2 transition-all ${
                                    isCurrent
                                        ? "border-[#5BC2CC] bg-[#E0F4F4]/60"
                                        : "border-transparent hover:bg-black/[0.03]"
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-0.5">
                                    <span
                                        className={`text-[10px] uppercase tracking-[0.2em] font-bold ${
                                            isCurrent
                                                ? "text-[#2E8E9A]"
                                                : "text-[#4a5662]"
                                        }`}
                                    >
                                        Slide {i + 1}
                                    </span>
                                    {isCurrent && (
                                        <Pill tone="primary">Now</Pill>
                                    )}
                                </div>
                                <p
                                    className={`text-[13px] leading-snug ${
                                        isCurrent
                                            ? "font-semibold text-[#14202a]"
                                            : "text-[#14202a]"
                                    }`}
                                >
                                    {s.title}
                                </p>
                                {isCurrent && (
                                    <div className="mt-2 space-y-1.5">
                                        {(s.notes || []).map((n, k) => (
                                            <p
                                                key={k}
                                                className="text-[11px] text-[#4a5662] leading-snug pl-2 border-l-2 border-[#5BC2CC]/40"
                                            >
                                                {n}
                                            </p>
                                        ))}
                                        {(s.misconceptions || []).length > 0 && (
                                            <div className="mt-2 px-2.5 py-1.5 rounded-lg bg-[#FFF1E5]">
                                                <div className="flex items-center gap-1 mb-0.5">
                                                    <AlertTriangle className="w-3 h-3 text-[#C75730]" />
                                                    <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#C75730]">
                                                        Watch for
                                                    </span>
                                                </div>
                                                {s.misconceptions.map((m, k) => (
                                                    <p
                                                        key={k}
                                                        className="text-[11px] text-[#7A3A20]"
                                                    >
                                                        {m}
                                                    </p>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </aside>

            {/* ----- Right: Preview + Dock ----- */}
            <main className="flex-1 min-h-screen px-8 py-6 pb-32">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold">
                            Now teaching
                        </p>
                        <h1 className="text-xl font-semibold tracking-tight">
                            {slide?.title || "Loading…"}
                        </h1>
                        <p className="text-[11px] text-[#4a5662] mt-0.5">
                            Slide {deckState.slide_no} of{" "}
                            {gaseousLesson.slides.length}
                            {totalFragments > 0
                                ? ` · Fragment ${Math.min(
                                      deckState.fragment_no + 1,
                                      totalFragments,
                                  )}/${totalFragments}`
                                : ""}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="teacher-deck-ai-mic-btn"
                            onClick={() => setIsVoiceModalOpen(true)}
                            className="relative group px-3.5 py-2 rounded-xl text-xs font-bold text-white flex items-center gap-1.5 transition-all active:scale-[0.97]"
                            style={{
                                background: "linear-gradient(135deg, #7C6BF2, #5BC2CC, #FFA987)",
                                boxShadow: "0 4px 14px rgba(124,107,242,0.4)"
                            }}
                            title="Speak to the classroom AI co-teacher"
                        >
                            <Mic className="w-3.5 h-3.5" />
                            Ask AI
                        </button>
                        <button
                            data-testid="teacher-deck-fullscreen-student-btn"
                            onClick={requestStudentFullscreen}
                            className="px-3 py-2 rounded-xl bg-white border border-black/[0.06] text-xs font-semibold flex items-center gap-1.5 hover:border-[#5BC2CC] transition-all"
                            title="Make the student screen fullscreen"
                        >
                            <Maximize2 className="w-3.5 h-3.5 text-[#2E8E9A]" />
                            Fullscreen student
                        </button>
                        <button
                            data-testid="teacher-deck-end-session-btn"
                            onClick={endSession}
                            className="px-3 py-2 rounded-xl bg-white border border-black/[0.06] text-xs font-semibold flex items-center gap-1.5 hover:border-[#C75730] hover:text-[#C75730] transition-all"
                        >
                            <Square className="w-3.5 h-3.5" />
                            End session
                        </button>
                        <Pill tone={bcReady ? "ok" : "neutral"}>
                            <Wifi className="w-3 h-3" />
                            {channel?.connected ? "Live · cloud" : "Local"}
                        </Pill>
                        {joinCode && (
                            <div
                                className="ml-1 px-3 py-1.5 rounded-xl bg-[#14202a] text-white flex items-center gap-2"
                                data-testid="deck-join-code-chip"
                                title="Open the Sparkl mobile app and enter this code to control from your phone"
                            >
                                <span className="text-[9px] uppercase tracking-[0.22em] text-[#B8E8E8] font-semibold">
                                    Phone code
                                </span>
                                <span className="text-[14px] font-bold tracking-[0.2em]">
                                    {joinCode}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Iframe preview (full width — mascot only on student panel) */}
                <div
                    className="rounded-2xl overflow-hidden border border-black/[0.06] soft-shadow bg-black relative"
                    style={{ aspectRatio: "16 / 9" }}
                    data-testid="teacher-deck-preview"
                >
                    <iframe
                        ref={iframeRef}
                        src={iframeSrc}
                        title="Gaseous state — teacher preview"
                        className="w-full h-full block"
                        sandbox="allow-scripts allow-same-origin allow-presentation allow-forms"
                    />
                    {/* Muted ribbon so teacher remembers this preview has no audio */}
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur text-white text-[10px] font-semibold uppercase tracking-[0.2em] flex items-center gap-1.5">
                        <Volume2 className="w-3 h-3" />
                        Preview · muted
                    </div>
                </div>

                {/* AI recommendations row */}
                {recs.length > 0 && (
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {recs.map((r) => (
                            <div
                                key={r.id}
                                data-testid={`deck-rec-${r.id}`}
                                className="rounded-2xl bg-white border border-black/[0.04] p-4 soft-shadow flex items-start gap-3"
                            >
                                <div className="w-8 h-8 rounded-lg rose-gradient flex items-center justify-center shrink-0">
                                    <Sparkles className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#C75730] font-semibold mb-1">
                                        AI suggestion
                                    </p>
                                    <p className="text-[13px] text-[#14202a] leading-snug">
                                        {r.text}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Real-life examples row — clickable to push to student board */}
                <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold flex items-center gap-2">
                            <BookOpen className="w-3 h-3" />
                            Real-life hooks for this lesson
                        </p>
                        {overlay?.type === "example" && (
                            <button
                                onClick={clearOverlay}
                                data-testid="deck-hide-example-btn"
                                className="text-[10px] uppercase tracking-[0.18em] text-[#C75730] font-semibold flex items-center gap-1 hover:underline"
                            >
                                <X className="w-3 h-3" />
                                Hide from student board
                            </button>
                        )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {(gaseousLesson.realLifeExamples || []).map((ex) => {
                            const isShown =
                                overlay?.type === "example" &&
                                overlay?.payload?.example?.id === ex.id;
                            return (
                                <button
                                    key={ex.id}
                                    data-testid={`deck-example-${ex.id}`}
                                    onClick={() =>
                                        pushOverlay({
                                            type: "example",
                                            payload: { example: ex },
                                        })
                                    }
                                    className={`text-left rounded-2xl overflow-hidden bg-white border-2 soft-shadow transition-all active:scale-[0.98] ${
                                        isShown
                                            ? "border-[#FFA987] ring-4 ring-[#FFA987]/20"
                                            : "border-transparent hover:border-[#FFA987]/40"
                                    }`}
                                >
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img
                                            src={ex.image}
                                            alt={ex.headline}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="p-3">
                                        <p className="text-[12px] font-semibold text-[#14202a] leading-tight">
                                            {ex.headline}
                                        </p>
                                        <p className="text-[11px] text-[#4a5662] mt-1 leading-snug line-clamp-3">
                                            {ex.description}
                                        </p>
                                        {isShown ? (
                                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C75730] font-semibold mt-2 flex items-center gap-1">
                                                <span className="w-1.5 h-1.5 rounded-full bg-[#FFA987] animate-pulse" />
                                                Live on student board
                                            </p>
                                        ) : (
                                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#5BC2CC] font-semibold mt-2">
                                                Click to show
                                            </p>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ===== Review panel ===== */}
                <ReviewPanel
                    overlay={overlay}
                    pushOverlay={pushOverlay}
                    clearOverlay={clearOverlay}
                />
            </main>

            <VoiceAssistantModal
                isOpen={isVoiceModalOpen}
                onClose={() => setIsVoiceModalOpen(false)}
                sessionChannel={channel}
                activeLesson={gaseousLesson}
                slideIndex={deckState.slide_no - 1}
                state={deckState}
                sendCommand={sendCommand}
            />

            {/* ----- Concept / Engagement check popup (mirrored from iframe) ----- */}
            <AnimatePresence>
                {popup && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 backdrop-blur-xl bg-white/10 flex items-center justify-center px-6"
                        data-testid="deck-popup-overlay"
                    >
                        <motion.div
                            initial={{ scale: 0.96, y: 10, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            exit={{ scale: 0.96, y: 10, opacity: 0 }}
                            transition={{ duration: 0.18 }}
                            className="max-w-2xl w-full bg-white rounded-3xl shadow-[0_30px_70px_rgba(20,32,42,0.25)] p-8 relative"
                        >
                            <button
                                onClick={() => setPopup(null)}
                                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/[0.05] hover:bg-black/[0.08] flex items-center justify-center"
                                aria-label="Close popup notice"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                            <Pill
                                tone={
                                    popup.popup_type === "engagement_check"
                                        ? "primary"
                                        : "warm"
                                }
                            >
                                <Brain className="w-3 h-3" />
                                {popup.popup_type === "engagement_check"
                                    ? "Engagement check"
                                    : "Concept check"}
                            </Pill>
                            <h3 className="text-2xl font-semibold tracking-tight mt-4 leading-snug">
                                {popup.question}
                            </h3>
                            <p className="text-[12px] text-[#4a5662] mt-3">
                                Shown on the student board. Discuss with the
                                class, then continue.
                            </p>
                            <div className="mt-6 flex items-center gap-3">
                                <button
                                    data-testid="deck-popup-next-btn"
                                    onClick={dismissPopup}
                                    className="px-5 py-3 rounded-2xl sparkl-gradient text-white font-semibold text-sm shadow-[0_10px_28px_rgba(91,194,204,0.36)] flex items-center gap-2"
                                >
                                    Got it · Next
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setPopup(null)}
                                    data-testid="deck-popup-dismiss-btn"
                                    className="px-4 py-3 rounded-2xl bg-white border border-black/[0.06] text-xs font-semibold hover:border-black/[0.12]"
                                >
                                    Keep discussing
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ----- Floating dock ----- */}
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30">
                <div className="bg-white/75 backdrop-blur-xl border border-white/60 rounded-2xl shadow-[0_18px_48px_rgba(20,32,42,0.18)] px-2.5 py-1.5 flex items-center gap-1">
                    <button
                        data-testid="deck-prev-btn"
                        onClick={prev}
                        title="Previous fragment / slide"
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-[#14202a] hover:bg-black/[0.05] active:scale-95 transition-all"
                    >
                        <SkipBack className="w-4 h-4" />
                    </button>
                    <button
                        data-testid="deck-play-pause-btn"
                        onClick={togglePlay}
                        title={playing ? "Pause narration" : "Resume narration"}
                        className="w-10 h-10 rounded-xl sparkl-gradient text-white flex items-center justify-center shadow-[0_4px_14px_rgba(91,194,204,0.35)] hover:shadow-[0_6px_18px_rgba(91,194,204,0.45)] active:scale-95 transition-all"
                    >
                        {playing ? (
                            <Pause className="w-4 h-4" />
                        ) : (
                            <Play className="w-4 h-4 pl-0.5" />
                        )}
                    </button>
                    <button
                        data-testid="deck-next-btn"
                        onClick={next}
                        title="Next fragment / slide"
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-[#14202a] hover:bg-black/[0.05] active:scale-95 transition-all"
                    >
                        <SkipForward className="w-4 h-4" />
                    </button>
                    <button
                        data-testid="deck-replay-btn"
                        onClick={replay}
                        title="Replay current audio"
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-[#14202a] hover:bg-black/[0.05] active:scale-95 transition-all"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <div className="w-px h-6 bg-black/[0.08] mx-1" />
                    <button
                        data-testid="deck-autopilot-btn"
                        onClick={() => setAutoPilot((v) => !v)}
                        title={
                            autoPilot
                                ? "Autopilot ON — slides advance automatically"
                                : "Autopilot OFF — stops at each slide end"
                        }
                        className={`h-9 rounded-xl px-2.5 flex items-center gap-1.5 text-[11px] font-semibold transition-all active:scale-95 ${
                            autoPilot
                                ? "bg-[#FFF1E5] text-[#C75730]"
                                : "text-[#14202a] hover:bg-black/[0.05]"
                        }`}
                    >
                        <Zap
                            className={`w-3.5 h-3.5 ${
                                autoPilot ? "text-[#C75730]" : "text-[#4a5662]"
                            }`}
                        />
                        Autopilot {autoPilot ? "ON" : "OFF"}
                    </button>
                    <div className="w-px h-6 bg-black/[0.08] mx-1" />
                    <div className="px-2.5 text-[10px] uppercase tracking-[0.2em] text-[#4a5662] font-semibold flex items-center gap-1.5">
                        <ChevronRight className="w-3 h-3" />
                        Slide {deckState.slide_no}/{gaseousLesson.slides.length}
                    </div>
                </div>
            </div>

            {/* Teacher-side "live now" banner — non-blocking, sits above the
                floating dock. Teacher keeps full access to the panel while a
                review overlay is on the student board. */}
            {overlay && (
                <div
                    className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 bg-[#14202a] text-white rounded-2xl shadow-[0_18px_48px_rgba(20,32,42,0.32)] px-4 py-2.5 flex items-center gap-3 max-w-[88vw]"
                    data-testid="deck-overlay-banner"
                >
                    <span className="w-2 h-2 rounded-full bg-[#5BC2CC] animate-pulse" />
                    <p className="text-[12px] leading-snug">
                        <span className="uppercase tracking-[0.2em] text-[#B8E8E8] text-[10px] font-semibold mr-2">
                            On student board
                        </span>
                        <span className="font-semibold">
                            {overlay.type === "example" &&
                                `Example · ${overlay.payload.example.headline}`}
                            {overlay.type === "mcq" &&
                                `MCQ · ${overlay.payload.mcq.stem.slice(0, 60)}…`}
                            {overlay.type === "subj" &&
                                `Practice · ${overlay.payload.q.stem.slice(0, 60)}…`}
                            {overlay.type === "activity" && "Activity · drag-drop"}
                            {overlay.type === "recap" && "Recap · summary"}
                        </span>
                    </p>
                    <button
                        onClick={clearOverlay}
                        data-testid="deck-overlay-banner-hide"
                        className="ml-2 px-2.5 py-1 rounded-lg bg-white/15 hover:bg-white/25 text-[11px] font-semibold flex items-center gap-1.5"
                    >
                        <X className="w-3 h-3" />
                        Hide
                    </button>
                </div>
            )}
        </div>
    );
};

/* ===== Review panel — MCQ / Subjective / Activity / Recap ===== */
const ReviewPanel = ({ overlay, pushOverlay, clearOverlay }) => {
    const [tab, setTab] = useState("mcq");
    const [mcqIdx, setMcqIdx] = useState(0);
    const [mcqSel, setMcqSel] = useState(null);
    const [mcqReveal, setMcqReveal] = useState(false);

    const [subjIdx, setSubjIdx] = useState(0);
    const [subjExpected, setSubjExpected] = useState(false);
    const [subjMistake, setSubjMistake] = useState(false);

    const [placements, setPlacements] = useState({});
    const [activityCheck, setActivityCheck] = useState(null);
    const [activityShowCorrect, setActivityShowCorrect] = useState(false);

    const tabs = [
        { id: "mcq", label: "Concept check", icon: Brain },
        { id: "subj", label: "Practice", icon: Sparkles },
        { id: "activity", label: "Activity", icon: Zap },
        { id: "recap", label: "Recap", icon: BookOpen },
    ];

    /* ---------- MCQ ---------- */
    const mcq = gaseousLesson.mcqs[mcqIdx];
    const sendMcq = (nextSel = mcqSel, nextReveal = mcqReveal) => {
        pushOverlay({
            type: "mcq",
            payload: {
                mcq,
                selectedId: nextSel,
                revealAnswer: nextReveal,
            },
        });
    };

    /* ---------- Subjective ---------- */
    const subj = gaseousLesson.subjectives[subjIdx];
    const sendSubj = (nextExp = subjExpected, nextMis = subjMistake) => {
        pushOverlay({
            type: "subj",
            payload: {
                q: subj,
                revealExpected: nextExp,
                revealMistake: nextMis,
            },
        });
    };

    /* ---------- Activity (drag-drop) — teacher-side drag UI ---------- */
    const activity = gaseousLesson.activity;
    const setPlacement = (itemId, catId) => {
        const next = { ...placements, [itemId]: catId };
        setPlacements(next);
        setActivityCheck(null);
        pushOverlay({
            type: "activity",
            payload: {
                activity,
                placements: next,
                checkResult: null,
                showCorrect: activityShowCorrect,
            },
        });
    };
    const checkActivity = () => {
        const total = activity.items.length;
        const correct = activity.items.filter(
            (it) => placements[it.id] === it.correct,
        ).length;
        const result = { correct, total };
        setActivityCheck(result);
        pushOverlay({
            type: "activity",
            payload: {
                activity,
                placements,
                checkResult: result,
                showCorrect: activityShowCorrect,
            },
        });
    };
    const toggleShowCorrect = () => {
        const next = !activityShowCorrect;
        setActivityShowCorrect(next);
        // Snap correct placements
        const snapped = {};
        activity.items.forEach((it) => {
            snapped[it.id] = next ? it.correct : placements[it.id];
        });
        if (next) setPlacements(snapped);
        pushOverlay({
            type: "activity",
            payload: {
                activity,
                placements: next ? snapped : placements,
                checkResult: activityCheck,
                showCorrect: next,
            },
        });
    };
    const resetActivity = () => {
        setPlacements({});
        setActivityCheck(null);
        setActivityShowCorrect(false);
        pushOverlay({
            type: "activity",
            payload: {
                activity,
                placements: {},
                checkResult: null,
                showCorrect: false,
            },
        });
    };

    return (
        <div
            className="mt-8 rounded-3xl bg-white/70 backdrop-blur border border-black/[0.04] soft-shadow"
            data-testid="deck-review-panel"
        >
            <div className="px-5 pt-5 pb-3 flex items-center gap-2 flex-wrap">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold mr-3">
                    Review
                </p>
                {tabs.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`deck-review-tab-${t.id}`}
                            className={`px-3 py-1.5 rounded-xl text-[11px] font-semibold flex items-center gap-1.5 transition-all ${
                                active
                                    ? "bg-[#14202a] text-white"
                                    : "bg-white text-[#14202a] border border-black/[0.06] hover:border-[#14202a]/30"
                            }`}
                        >
                            <Icon className="w-3 h-3" />
                            {t.label}
                        </button>
                    );
                })}
                <span className="ml-auto" />
                {overlay && overlay.type !== "example" && (
                    <button
                        onClick={clearOverlay}
                        data-testid="deck-overlay-hide-btn"
                        className="px-3 py-1.5 rounded-xl text-[11px] font-semibold text-[#C75730] bg-[#FFF1E5] hover:bg-[#FFE4D2] flex items-center gap-1.5"
                    >
                        <X className="w-3 h-3" />
                        Hide from student board
                    </button>
                )}
            </div>
            <div className="px-5 pb-5">
                {tab === "mcq" && (
                    <div data-testid="deck-review-mcq">
                        <div className="flex items-center gap-2 mb-3">
                            <Pill tone="primary">
                                Q{mcqIdx + 1}/{gaseousLesson.mcqs.length}
                            </Pill>
                            <div className="flex items-center gap-1 ml-auto">
                                <button
                                    onClick={() => {
                                        setMcqIdx((i) => Math.max(0, i - 1));
                                        setMcqSel(null);
                                        setMcqReveal(false);
                                    }}
                                    disabled={mcqIdx === 0}
                                    className="px-2 py-1 rounded-lg text-[11px] font-semibold border border-black/[0.06] disabled:opacity-40"
                                    data-testid="deck-mcq-prev"
                                >
                                    Prev
                                </button>
                                <button
                                    onClick={() => {
                                        setMcqIdx((i) =>
                                            Math.min(
                                                gaseousLesson.mcqs.length - 1,
                                                i + 1,
                                            ),
                                        );
                                        setMcqSel(null);
                                        setMcqReveal(false);
                                    }}
                                    disabled={
                                        mcqIdx >=
                                        gaseousLesson.mcqs.length - 1
                                    }
                                    className="px-2 py-1 rounded-lg text-[11px] font-semibold border border-black/[0.06] disabled:opacity-40"
                                    data-testid="deck-mcq-next"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                        <h3 className="text-[15px] font-semibold leading-snug text-[#14202a]">
                            {mcq.stem}
                        </h3>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                            {mcq.options.map((opt) => {
                                const isSel = mcqSel === opt.id;
                                return (
                                    <button
                                        key={opt.id}
                                        onClick={() => {
                                            setMcqSel(opt.id);
                                            sendMcq(opt.id, mcqReveal);
                                        }}
                                        data-testid={`deck-mcq-pick-${opt.id}`}
                                        className={`px-3 py-2.5 rounded-xl text-[13px] text-left border-2 transition-all ${
                                            isSel
                                                ? "border-[#5BC2CC] bg-[#E0F4F4]"
                                                : "border-black/[0.06] bg-white hover:border-[#5BC2CC]/40"
                                        }`}
                                    >
                                        {opt.text}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                            <button
                                onClick={() => sendMcq(mcqSel, mcqReveal)}
                                data-testid="deck-mcq-push"
                                className="px-3 py-1.5 rounded-xl text-[11px] font-semibold sparkl-gradient text-white"
                            >
                                Show on student board
                            </button>
                            <button
                                onClick={() => {
                                    const next = !mcqReveal;
                                    setMcqReveal(next);
                                    sendMcq(mcqSel, next);
                                }}
                                data-testid="deck-mcq-reveal"
                                className="px-3 py-1.5 rounded-xl text-[11px] font-semibold bg-white border border-black/[0.06]"
                            >
                                {mcqReveal ? "Hide answer" : "Reveal answer"}
                            </button>
                        </div>
                    </div>
                )}

                {tab === "subj" && (
                    <div data-testid="deck-review-subj">
                        <div className="flex items-center gap-2 mb-3">
                            <Pill tone="warm">
                                Q{subjIdx + 1}/
                                {gaseousLesson.subjectives.length}
                            </Pill>
                            <div className="flex items-center gap-1 ml-auto">
                                <button
                                    onClick={() => {
                                        setSubjIdx((i) =>
                                            Math.max(0, i - 1),
                                        );
                                        setSubjExpected(false);
                                        setSubjMistake(false);
                                    }}
                                    disabled={subjIdx === 0}
                                    className="px-2 py-1 rounded-lg text-[11px] font-semibold border border-black/[0.06] disabled:opacity-40"
                                >
                                    Prev
                                </button>
                                <button
                                    onClick={() => {
                                        setSubjIdx((i) =>
                                            Math.min(
                                                gaseousLesson.subjectives
                                                    .length - 1,
                                                i + 1,
                                            ),
                                        );
                                        setSubjExpected(false);
                                        setSubjMistake(false);
                                    }}
                                    disabled={
                                        subjIdx >=
                                        gaseousLesson.subjectives.length - 1
                                    }
                                    className="px-2 py-1 rounded-lg text-[11px] font-semibold border border-black/[0.06] disabled:opacity-40"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                        <h3 className="text-[15px] font-semibold leading-snug text-[#14202a]">
                            {subj.stem}
                        </h3>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <button
                                onClick={() => sendSubj(subjExpected, subjMistake)}
                                className="px-3 py-1.5 rounded-xl text-[11px] font-semibold sparkl-gradient text-white"
                                data-testid="deck-subj-push"
                            >
                                Show question on student board
                            </button>
                            <button
                                onClick={() => {
                                    const next = !subjExpected;
                                    setSubjExpected(next);
                                    sendSubj(next, subjMistake);
                                }}
                                className="px-3 py-1.5 rounded-xl text-[11px] font-semibold bg-white border border-black/[0.06]"
                                data-testid="deck-subj-reveal-expected"
                            >
                                {subjExpected ? "Hide" : "Reveal"} expected
                            </button>
                            <button
                                onClick={() => {
                                    const next = !subjMistake;
                                    setSubjMistake(next);
                                    sendSubj(subjExpected, next);
                                }}
                                className="px-3 py-1.5 rounded-xl text-[11px] font-semibold bg-white border border-black/[0.06]"
                                data-testid="deck-subj-reveal-mistake"
                            >
                                {subjMistake ? "Hide" : "Reveal"} common mistake
                            </button>
                        </div>
                    </div>
                )}

                {tab === "activity" && (
                    <div data-testid="deck-review-activity">
                        <h3 className="text-[15px] font-semibold text-[#14202a] leading-snug">
                            {activity.title}
                        </h3>
                        <p className="text-[12px] text-[#4a5662] mt-1">
                            {activity.instruction}
                        </p>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                            {activity.categories.map((cat) => (
                                <div
                                    key={cat.id}
                                    className="rounded-xl bg-[#F5FAFC] border-2 border-dashed border-[#B8E8E8] p-3 min-h-[110px]"
                                >
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#2E8E9A] font-semibold mb-2">
                                        {cat.label}
                                    </p>
                                    <div className="space-y-1.5">
                                        {activity.items
                                            .filter(
                                                (it) =>
                                                    placements[it.id] ===
                                                    cat.id,
                                            )
                                            .map((it) => (
                                                <div
                                                    key={it.id}
                                                    className="px-2 py-1.5 rounded-lg bg-white border border-black/[0.06] text-[12px]"
                                                >
                                                    {it.text}
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3">
                            <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2">
                                Place each item
                            </p>
                            <div className="space-y-2">
                                {activity.items
                                    .filter((it) => !placements[it.id])
                                    .map((it) => (
                                        <div
                                            key={it.id}
                                            className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white border border-black/[0.06]"
                                        >
                                            <span className="text-[13px] flex-1">
                                                {it.text}
                                            </span>
                                            {activity.categories.map((c) => (
                                                <button
                                                    key={c.id}
                                                    onClick={() =>
                                                        setPlacement(
                                                            it.id,
                                                            c.id,
                                                        )
                                                    }
                                                    className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-[#F5FAFC] hover:bg-[#E0F4F4]"
                                                    data-testid={`deck-activity-place-${it.id}-${c.id}`}
                                                >
                                                    → {c.label}
                                                </button>
                                            ))}
                                        </div>
                                    ))}
                            </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <button
                                onClick={checkActivity}
                                disabled={
                                    Object.keys(placements).length !==
                                    activity.items.length
                                }
                                className="px-3 py-1.5 rounded-xl text-[11px] font-semibold sparkl-gradient text-white disabled:opacity-40"
                                data-testid="deck-activity-check"
                            >
                                Check answers
                            </button>
                            <button
                                onClick={toggleShowCorrect}
                                className="px-3 py-1.5 rounded-xl text-[11px] font-semibold bg-white border border-black/[0.06]"
                                data-testid="deck-activity-show-correct"
                            >
                                {activityShowCorrect
                                    ? "Hide correct"
                                    : "Show correct"}
                            </button>
                            <button
                                onClick={resetActivity}
                                className="px-3 py-1.5 rounded-xl text-[11px] font-semibold bg-white border border-black/[0.06]"
                                data-testid="deck-activity-reset"
                            >
                                Reset
                            </button>
                            {activityCheck && (
                                <span className="text-[12px] text-[#14202a] ml-2">
                                    {activityCheck.correct} / {activityCheck.total}{" "}
                                    correct
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {tab === "recap" && (
                    <div data-testid="deck-review-recap">
                        <h3 className="text-[15px] font-semibold text-[#14202a]">
                            {gaseousLesson.recap.title}
                        </h3>
                        <ul className="mt-3 space-y-2">
                            {gaseousLesson.recap.bullets.map((b, i) => (
                                <li
                                    key={i}
                                    className="text-[13px] text-[#14202a] flex items-start gap-2"
                                >
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full sparkl-gradient shrink-0" />
                                    {b}
                                </li>
                            ))}
                        </ul>
                        <button
                            onClick={() =>
                                pushOverlay({
                                    type: "recap",
                                    payload: { recap: gaseousLesson.recap },
                                })
                            }
                            className="mt-4 px-3 py-1.5 rounded-xl text-[11px] font-semibold sparkl-gradient text-white"
                            data-testid="deck-recap-push"
                        >
                            Show on student board
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// Keep a tiny reference to gaseousStoryboard so tree-shaking doesn't drop it
// in case future builds remove unused exports — the bridge reads it from
// localStorage, but Audio Studio uses it as the default.
export const __keepStoryboard = gaseousStoryboard;

export default TeacherPanelDeck;
